/**
 * AuditLogPanel — UNIFIED activity log panel used across the platform.
 *
 * This file used to be the panel AND the design: a 100-entry table pairing
 * every action with its own Tailwind `text-*`/`bg-*` colour pair (teal, sky,
 * slate, violet, cyan — none of them brand tokens), a hand-rolled pulse
 * skeleton, a hand-rolled "Load more" pill, and its own `formatDateTime`
 * absolute timestamps while the feed two panes over showed "3d ago".
 *
 * It is now a data source. Fetching and pagination live here — the endpoint
 * per entity type is the only thing about an audit log that is actually
 * entity-specific — and everything visual is `shared/activity`:
 *
 *   raw rows → `activityModel.toActivityEntries` → `<ActivityPane>`
 *
 * Routes the fetch to the right backend endpoint based on entityType:
 *   matter   → GET /matters/{uuid}/audit-log
 *   task     → GET /tasks/{uuid}/audit-log
 *   project  → GET /projects/{uuid}/audit-log
 *   review   → GET /reviews/{uuid}/audit-log
 *   * (else) → GET /audit-logs/entity/{Type}/{uuid}
 *
 * Resource-specific wrappers (MatterAuditLog, TaskAuditLog, ProjectAuditLog,
 * ReviewAuditLog) are thin shells around this component so existing call sites
 * keep working.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiXLight, PiClockCounterClockwiseLight, PiArrowsClockwiseLight } from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import apis from '../../utils/apis';
import { fetchEntityAuditLog } from '../../store/actions/AuditLogActions';
import Button from '../shared/TailwindComponents/Button';
import { useInPane } from '../shared/surface/PaneBody';
import PaneFooter from '../shared/surface/PaneFooter';
import ActivityPane from '../shared/activity/ActivityPane';
import {
  ActivityEntry,
  AuditRecord,
  mergeActivityEntries,
  normalizeAuditPayload,
  toActivityEntries,
} from '../shared/activity/activityModel';

/** Kept as the panel's public row type — the model owns the shape. */
export type AuditEntry = AuditRecord;
export type { AuditDiff } from '../shared/activity/activityModel';

export interface AuditLogPanelProps {
  entityType: string;
  entityId: string;
  entityName?: string;
  onClose: () => void;
  /** Optional: clicking an entry triggers this. ReviewSessionDetail uses it
   *  to jump to a finding. Most consumers leave it undefined. */
  onEntryClick?: (entry: AuditEntry) => void;
  /**
   * Drop the panel's own title bar.
   *
   * Rendered INSIDE a surface pane this is automatic — the pane already has a
   * header and a close button, and two stacked read as a bug, so the default
   * comes from `useInPane()`. The prop stays for the hosts that provide their
   * own header WITHOUT being a pane (the global side panel does), and an
   * explicit value always wins.
   */
  hideHeader?: boolean;
  /**
   * Already-normalized entries from a SECOND store, merged into the audit
   * rows by timestamp.
   *
   * The task page passes its feed through here: a feed post is written to its
   * own SQL table and never reaches the audit trail, so without this the
   * Activity pane stays silent about something the user just wrote. The panel
   * still owns the audit fetch; these ride alongside it.
   */
  extraEntries?: ActivityEntry[];
  /**
   * Refresh hook for whatever produced `extraEntries` — run alongside the
   * audit re-fetch so one Refresh press reloads both stores.
   */
  onRefreshExtra?: () => Promise<void> | void;
}

const AuditLogPanel: React.FC<AuditLogPanelProps> = ({
  entityType,
  entityId,
  entityName,
  onClose,
  onEntryClick,
  hideHeader,
  extraEntries,
  onRefreshExtra,
}) => {
  const { t } = useTranslation();
  const inPane = useInPane();
  const withoutHeader = hideHeader ?? inPane;
  const dispatch = useAppDispatch();
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const loadEntries = useCallback(async (pageNum: number = 1) => {
    setIsLoading(true);
    try {
      let raw: any;
      const lower = (entityType || '').toLowerCase();
      if (lower === 'matter') {
        const resp = await apis.get(`/matters/${entityId}/audit-log`, { params: { page: pageNum, limit: 50 } });
        raw = resp?.data;
      } else if (lower === 'task') {
        const resp = await apis.get(`/tasks/${entityId}/audit-log`, { params: { page: pageNum, limit: 50 } });
        raw = resp?.data;
      } else if (lower === 'review' || lower === 'review_session') {
        const resp = await apis.get(`/reviews/${entityId}/audit-log`, { params: { page: pageNum, page_size: 50 } });
        raw = resp?.data;
      } else if (lower === 'draft') {
        const resp = await apis.get(`/drafting/drafts/${entityId}/audit-log`, { params: { page: pageNum, limit: 50 } });
        raw = resp?.data;
      } else if (lower === 'strategy' || lower === 'insight') {
        const resp = await apis.get(`/drafting/insights/${entityId}/audit-log`, { params: { page: pageNum, limit: 50 } });
        raw = resp?.data;
      } else {
        // Generic entity audit log endpoint expects PascalCase entity_type
        const pascal = entityType.charAt(0).toUpperCase() + entityType.slice(1);
        raw = await dispatch(fetchEntityAuditLog(pascal, entityId, pageNum, 50) as any);
      }
      const { entries: list, hasNext } = normalizeAuditPayload(raw);
      const mapped = toActivityEntries(list);
      if (pageNum === 1) {
        setEntries(mapped);
      } else {
        setEntries((prev) => [...prev, ...mapped]);
      }
      setHasMore(hasNext);
      setPage(pageNum);
    } catch {
      if (pageNum === 1) setEntries([]);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, entityId, entityType]);

  useEffect(() => { loadEntries(1); }, [loadEntries]);

  /** One Refresh press reloads both stores. */
  const refreshAll = useCallback(async () => {
    await Promise.all([loadEntries(1), onRefreshExtra?.()]);
  }, [loadEntries, onRefreshExtra]);

  /* The audit rows and whatever the host handed us, on one rail in time
     order. `useMemo` because a merge on every keystroke of a parent re-render
     would re-sort the whole log. */
  const timeline = React.useMemo(
    () => (extraEntries?.length ? mergeActivityEntries(entries, extraEntries) : entries),
    [entries, extraEntries]
  );

  return (
    <>
      {!withoutHeader && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-hairline-soft flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <PiClockCounterClockwiseLight className="w-5 h-5 text-fg-muted flex-shrink-0" />
              <h3 className="text-sm text-fg-strong truncate">{t('Activity Log')}</h3>
            </div>
            {entityName && (
              <p className="text-xs text-fg-muted mt-0.5 truncate">{entityName}</p>
            )}
          </div>
          <Button variant="neutral" size="small" onClick={onClose} ariaLabel={t('Close')}>
            <PiXLight className="w-4 h-4" />
          </Button>
        </div>
      )}

      <ActivityPane
        className="flex-1 min-h-0 px-5 py-4"
        entries={timeline}
        loading={isLoading}
        onRefresh={refreshAll}
        /* In a pane the refresh belongs to the pane's own footer, bottom
           right, next to the entry count — the footer pattern every pane
           follows. Outside one the list keeps its own toolbar. */
        hideToolbar={inPane}
        // Inside a pane the panel is already nested in a glass card, so the
        // rows are wells rather than a second layer of cards.
        density={inPane ? 'pane' : 'canvas'}
        hasMore={hasMore}
        onLoadMore={() => loadEntries(page + 1)}
        onEntryClick={
          onEntryClick ? (entry) => onEntryClick(entry.raw as AuditEntry) : undefined
        }
      />

      {inPane && (
        <PaneFooter
          count={timeline.length}
          label={timeline.length === 1 ? t('event') : t('events')}
        >
          <Button
            variant="outline"
            size="small"
            onClick={refreshAll}
            disabled={isLoading}
            isLoading={isLoading}
            ariaLabel={t('Refresh')}
            icon={<PiArrowsClockwiseLight className="h-4 w-4" />}
          >
            {t('Refresh')}
          </Button>
        </PaneFooter>
      )}
    </>
  );
};

export default AuditLogPanel;
